import { ClassNames } from '@emotion/react';
import { useLingui } from '@lingui/react/macro';
import { Calendar } from 'lucide-react';
import React, { forwardRef } from 'react';

import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import UDInput, { UDInputProps } from 'ud-form/components/input/component';
import CalendarHeader from './calendar-header';
import { CalendarMotion, useCalendarLayer } from './calendar-motion';
import { calendarStyles, popperStyles, wrapperStyles } from './calendar-styles';

export type UDDateInputProps = Omit<ReactDatePickerProps, 'dateFormat' | 'customInput' | 'placeholderText'> &
  Pick<UDInputProps, 'hasError' | 'valid'> & {
    /** Экземпляр react-datepicker — чтобы открыть календарь программно (`setOpen`, `setFocus`). `ref` занят полем ввода. */
    pickerRef?: React.Ref<DatePicker>;
    /** Как календарь появляется и закрывается. По умолчанию — «выезжает» от поля; диапазон меняет это на переходе «С» → «По». */
    calendarMotion?: Partial<CalendarMotion>;
  };

export const DATE_FORMAT = 'dd.MM.yyyy';

/**
 *
 * https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments
 */
export const parseFromString = (value: string | Date | null | undefined) => (value ? new Date(value) : null);

const UDDateInput = forwardRef<HTMLInputElement, UDDateInputProps>((props: UDDateInputProps, ref) => {
  const {
    value,
    hasError,
    valid,
    maxDate,
    minDate,
    wrapperClassName,
    calendarClassName,
    popperClassName,
    pickerRef,
    calendarMotion,
    ...datePickerProps
  } = props;
  const { t } = useLingui();
  const CalendarLayer = useCalendarLayer(calendarMotion);

  return (
    <ClassNames>
      {({ css, cx, theme }) => (
        <DatePicker
          ref={pickerRef}
          selected={parseFromString(value)}
          dateFormat={DATE_FORMAT}
          placeholderText={t({ message: 'дд.мм.гггг', comment: 'Маска поля даты: день, месяц, год' })}
          customInput={<UDInput ref={ref} iconProps={{ component: <Calendar size={16} /> }} hasError={hasError} valid={valid} />}
          renderCustomHeader={(header) => (
            <CalendarHeader {...header} minDate={parseFromString(minDate)} maxDate={parseFromString(maxDate)} />
          )}
          showPopperArrow={false}
          popperContainer={CalendarLayer}
          {...datePickerProps}
          wrapperClassName={cx(css(wrapperStyles()), wrapperClassName)}
          calendarClassName={cx(css(calendarStyles(theme)), calendarClassName)}
          popperClassName={cx(css(popperStyles(theme)), popperClassName)}
          minDate={parseFromString(minDate)}
          maxDate={parseFromString(maxDate)}
        />
      )}
    </ClassNames>
  );
});

export default UDDateInput;
